import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Form, SubmitButton } from '@components/shared/forms';

import { Button, Notification, CustomIcon } from '@components/shared';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import api from '@shared/api';
import routes from '@root/routes';
import actions from '@store/actions/saved';
import searchPageActions from '@store/actions/listingSearchPage/analyticsActions';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearch/utils';
import cs from 'classnames';
import qs from 'qs';
import s from './SaveSearchButton.module.less';
import CreateNewSearchForm from './CreateNewSearchForm';

type Props = {
  criteria: ListingSearchCriteria;
  disabled?: boolean;
};

export type FormValues = {
  name: string;
  useSearchCriteriaAsName: boolean;
};

const initialValues = { name: '', useSearchCriteriaAsName: false } as FormValues;

const SaveSearchButton = ({ criteria, disabled = false }: Props) => {
  const { t } = useTranslation('filters');

  const dispatch = useDispatch();
  const [searchSaved, setSearchSaved] = useState(false);
  const [isSaveNewSearchDialogOpen, setisSaveNewSearchDialogOpen] = useState(false);
  const [internalCriteria, setInternalCriteria] = useState<ListingSearchCriteria | null>(null);
  const [description, setSearchDescription] = useState<string>('');
  const savedSearchLinkClicked = () => {
    dispatch(searchPageActions.savedSearchNotificationLinkClicked());
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('saveSearch:nameRequiredErrorMessage')),
    useSearchCriteriaAsName: Yup.boolean(),
  });

  useEffect(() => {
    if (JSON.stringify(internalCriteria) !== JSON.stringify(criteria)) {
      setInternalCriteria(criteria);
    }
  }, [criteria]);

  useEffect(() => {
    setSearchSaved(false);
  }, [internalCriteria]);

  const componentRef = useRef<HTMLDivElement>(null!);

  const saveTheCurrentSearch = async (values: FormValues) => {
    if (searchSaved) return;

    let params = criteria.toSavedSearchParameters();
    if (!values.useSearchCriteriaAsName) {
      params = { ...criteria.toSavedSearchParameters(), ...{ name: values.name } };
    }

    const response = await api.post(routes.api.savedSearches, params);
    if (response.ok) {
      dispatch(actions.savedASearch());
      setisSaveNewSearchDialogOpen(false);
      Notification.saveSuccess({
        title: t('saveSearch:saved'),
        placement: 'topRight',
        top: 102,
        duration: 4,
        /* eslint-disable react/jsx-no-literals */
        text: (
          <Trans i18nKey="viewInFavorites" ns="saveSearch">
            You’ll get notified when a listing matches your search. View or edit it in your
            <a href={routes.savedSearches} onClick={savedSearchLinkClicked}>
              saved searches.
            </a>
          </Trans>
        ),
      });
      setSearchSaved(true);
    }
  };

  const fetchSavedSearchDescription = async () => {
    const params = criteria.toSavedSearchParameters();
    const response = await api.fetch(
      `${routes.api.savedSearchDescription}?${qs.stringify(params)}`,
    );

    if (response.ok) {
      const body = await response.json();
      setSearchDescription(body.description);
    }
  };

  const handleToggleSaveNewSearchDialog = () => {
    if (searchSaved) {
      return;
    }
    setisSaveNewSearchDialogOpen(!isSaveNewSearchDialogOpen);
  };

  useEffect(() => {
    if (isSaveNewSearchDialogOpen) {
      fetchSavedSearchDescription();
    }
  }, [isSaveNewSearchDialogOpen]);

  const handleKeyDown = event => {
    if (event.key === 'Escape') setisSaveNewSearchDialogOpen(false);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [componentRef]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (componentRef && !componentRef.current?.contains(event.target)) {
        setisSaveNewSearchDialogOpen(false);
      }
    };
    document.addEventListener('mouseup', handleOutsideClick);
    return () => {
      document.removeEventListener('mouseup', handleOutsideClick);
    };
  }, [componentRef]);

  return (
    <div ref={componentRef}>
      <Button
        className={cs({ [s.filled]: searchSaved }, s.button)}
        onClick={handleToggleSaveNewSearchDialog}
        type="tertiary"
        size="small"
        disabled={disabled}
        icon={searchSaved ? 'heart' : 'heart-outline'}
      >
        {searchSaved ? t('saveSearch:savedTitle') : t('saveSearch:unsavedTitle')}
      </Button>
      {isSaveNewSearchDialogOpen && (
        <dialog open={isSaveNewSearchDialogOpen}>
          <div className={s.overlay}>
            <div className={s.overlayHeader}>
              <span className={s.overlayTitle}>{t('saveSearch:nameSearchDialogTitle')}</span>
              <button
                onClick={handleToggleSaveNewSearchDialog}
                type="button"
                className={s.overlayCloseButton}
                data-testid="close-save-search-dialog"
              >
                <CustomIcon type="close" />
              </button>
            </div>
            <Form<FormValues>
              id="createSavedSearch"
              initialValues={initialValues}
              onSubmit={async values => {
                saveTheCurrentSearch(values);
              }}
              validationSchema={validationSchema}
            >
              <CreateNewSearchForm description={description} />
              <div className={s.saveSearchFromDialogButtonWrapper}>
                <SubmitButton size="small" className={s.saveSearchFromDialogButton}>
                  {t('saveSearch:saveSearchSubmitButton')}
                </SubmitButton>
              </div>
            </Form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default SaveSearchButton;
